
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Send, Mic, MicOff, FileText, Save, Volume2, VolumeX, Loader2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Language } from "@/utils/translations";
import ModernFAQSection from "./ModernFAQSection";
import { ChatMessage } from "@/types/chat";

interface ModernChatInterfaceProps {
  input: string;
  setInput: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  isListening: boolean;
  hasReachedLimit: boolean;
  openaiClient: any;
  currentQuestion: string;
  currentResponse: string;
  startListening: () => void;
  stopListening: () => void;
  onSaveChat: () => void;
  isSaving: boolean;
  language: Language;
  onLanguageChange: (lang: Language) => void;
  availableLanguages: Language[];
  userPlan: string | null;
  remainingRequests: number | string;
  conversationHistory?: ChatMessage[];
  onClearConversation?: () => void;
  isSpeaking?: boolean;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

// Helper function to extract text content from ChatMessage content
const getMessageContent = (content: ChatMessage['content']): string => {
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .map(part => {
        if ('text' in part) {
          return part.text;
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

const ModernChatInterface = ({
  input,
  setInput,
  onSubmit,
  isLoading,
  isListening,
  hasReachedLimit,
  openaiClient,
  currentQuestion,
  currentResponse,
  startListening,
  stopListening,
  onSaveChat,
  isSaving,
  language,
  onLanguageChange,
  availableLanguages,
  userPlan,
  remainingRequests,
  conversationHistory = [],
  onClearConversation,
  isSpeaking = false,
  isMuted = false,
  onToggleMute
}: ModernChatInterfaceProps) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream">
      {/* Main Content */}
      <main className="px-4 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center pt-10 pb-8 mb-8">
            <h1 className="text-2xl md:text-4xl font-bold text-navy leading-tight mb-3">
              Resuelve tus dudas migratorias con confianza
            </h1>
            <p className="text-base text-coolGray max-w-[600px] mx-auto leading-relaxed pb-8">
              Una herramienta confiable diseñada para orientarte con claridad y precisión.
            </p>
          </div>

          {/* Usage Info */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-green-600 px-6 py-3 rounded-xl text-sm font-medium border border-green-200 shadow-md">
              <span>✨ Acceso Ilimitado - Haz todas las preguntas que quieras</span>
              {conversationHistory.length > 0 && onClearConversation && (
                <>
                  <span>•</span>
                  <Button
                    onClick={onClearConversation}
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Limpiar Chat
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Chat Interface */}
          <Card className="shadow-2xl border-0 mb-12 overflow-hidden bg-white/90 backdrop-blur-sm hover:shadow-3xl transition-all duration-500">
            <CardContent className="p-8 md:p-12">
              {/* Voice Input Instruction */}
              <div className="text-center mb-8">
                <p className="text-coolGray mb-6 text-lg">
                  {conversationHistory.length > 0 ? 'Continúa la conversación' : 'Haz tu pregunta por voz o escríbela'}
                </p>
                
                {/* Voice Button */}
                <div className="flex justify-center mb-8">
                  <Button
                    onClick={isListening ? stopListening : startListening}
                    className={`w-20 h-20 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl ${
                      isListening 
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse scale-110' 
                        : 'bg-emerald hover:bg-emerald-hover hover:scale-110'
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-8 w-8 animate-spin text-white" />
                    ) : isListening ? (
                      <MicOff className="h-8 w-8 text-white" />
                    ) : (
                      <Mic className="h-8 w-8 text-white" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Text Input Form with Voice Controls */}
              <form onSubmit={onSubmit} className="space-y-6 mb-8">
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      placeholder="Escribe tu pregunta sobre inmigración..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      disabled={isLoading || isListening}
                      className="h-16 text-lg pl-6 pr-16 border-2 border-coolGray/30 focus:border-teal rounded-xl shadow-md bg-white/95 backdrop-blur-sm transition-all duration-300 hover:shadow-lg focus:shadow-lg"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={isLoading || isListening || !input.trim() || !openaiClient}
                      className="absolute right-2 top-2 h-12 w-12 bg-teal hover:bg-teal-hover rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      {isLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <Send className="h-6 w-6" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Voice Controls Button */}
                  {onToggleMute && (
                    <Button
                      onClick={onToggleMute}
                      variant="outline"
                      size="icon"
                      className={`h-16 w-16 rounded-xl transition-all duration-200 border-2 ${
                        isMuted 
                          ? 'border-red-300 text-red-500 hover:text-red-600 hover:bg-red-50' 
                          : 'border-teal/30 text-teal hover:text-teal-600 hover:bg-teal-50'
                      } ${isSpeaking ? 'animate-pulse' : ''} bg-white/95 backdrop-blur-sm shadow-md hover:shadow-lg`}
                      title={isMuted ? 'Activar audio' : 'Silenciar audio'}
                    >
                      {isMuted ? (
                        <VolumeX className="h-6 w-6" />
                      ) : (
                        <Volume2 className={`h-6 w-6 ${isSpeaking ? 'text-green-500' : ''}`} />
                      )}
                    </Button>
                  )}
                </div>
              </form>

              {/* Current Question Display */}
              {currentQuestion && (
                <div className="mb-8 p-6 bg-teal/5 rounded-xl border-l-4 border-teal shadow-sm">
                  <h4 className="font-semibold text-navy mb-3 text-lg">Tu Pregunta:</h4>
                  <p className="text-coolGray leading-relaxed">{currentQuestion}</p>
                </div>
              )}

              {/* Response Display */}
              {(currentResponse || isLoading) && (
                <div className="mb-8 p-8 bg-white rounded-xl min-h-[140px] border border-coolGray/10 shadow-md">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="flex space-x-3">
                        <div className="w-4 h-4 rounded-full bg-teal animate-bounce"></div>
                        <div className="w-4 h-4 rounded-full bg-teal animate-bounce delay-75"></div>
                        <div className="w-4 h-4 rounded-full bg-teal animate-bounce delay-150"></div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-navy text-lg">Respuesta:</h4>
                        {isSpeaking && (
                          <div className="flex items-center gap-2 text-green-600 text-sm">
                            <Volume2 className="h-4 w-4 animate-pulse" />
                            <span>Reproduciendo...</span>
                          </div>
                        )}
                      </div>
                      <p className="text-coolGray leading-relaxed whitespace-pre-wrap text-base">{currentResponse}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Conversation History */}
              {conversationHistory.length > 0 && (
                <div className="max-h-96 overflow-y-auto space-y-4">
                  <h3 className="text-lg font-semibold text-navy mb-4">Historial de Conversación</h3>
                  {conversationHistory.map((message, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl ${
                        message.role === 'user'
                          ? 'bg-teal/10 border-l-4 border-teal ml-8'
                          : 'bg-gray-50 border-l-4 border-gray-300 mr-8'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          message.role === 'user'
                            ? 'bg-teal text-white'
                            : 'bg-gray-400 text-white'
                        }`}>
                          {message.role === 'user' ? 'Tú' : 'IA'}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-coolGray leading-relaxed whitespace-pre-wrap">
                            {getMessageContent(message.content)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Button
              onClick={() => navigate('/procesa-documentos')}
              className="bg-emerald hover:bg-emerald-hover text-white px-10 py-4 rounded-xl text-lg font-medium shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <FileText className="h-6 w-6 mr-3" />
              Subir Documentos
            </Button>
            
            <Button
              onClick={onSaveChat}
              disabled={isSaving || !currentQuestion || !currentResponse}
              variant="outline"
              className="border-2 border-teal text-teal hover:bg-teal hover:text-white px-10 py-4 rounded-xl text-lg font-medium shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-3 h-6 w-6" />
                  Guardar Chat
                </>
              )}
            </Button>
          </div>

          {/* FAQ Section */}
          <ModernFAQSection />
        </div>
      </main>
    </div>
  );
};

export default ModernChatInterface;
